import {
	createContext,
	PropsWithChildren,
	useContext,
	useEffect,
	useState,
} from 'react';
import { clearOauthData, initialOauthState, OauthData, readOauthData, setOauthData } from './utils';

type AuthContextType = {
	oauth: OauthData;
	saveOauth: (newToken: OauthData | null) => void;
	clearOauth: () => void;
};

const AuthContext = createContext<AuthContextType>({
	oauth: readOauthData(),
	saveOauth: () => {},
	clearOauth: () => {},
});

const OAUTH_UPDATED_EVENT = 'oauth-updated';

export const AuthProvider: React.FC<PropsWithChildren> = ({ children }) => {
	const [oauth, setOauthDataState] = useState<OauthData>(() => {
		return readOauthData();
	});
	useEffect(() => {
		const handleStorage = (event: StorageEvent) => {
			// @ts-expect-error The variable is injected by webpack.DefinePlugin.
			if (event.key === HPP_OAUTH2_KEY) {
				const parsedData = JSON.parse( '' + event.newValue ) as OauthData;
				setOauthData( parsedData );
			}
		};

		const handleOAuthEvent = (event: Event) => {
			const customEvent = event as CustomEvent<OauthData>;
			setOauthDataState(customEvent.detail);
		};

		window.addEventListener('storage', handleStorage);
		window.addEventListener(OAUTH_UPDATED_EVENT, handleOAuthEvent);

		return () => {
			window.removeEventListener('storage', handleStorage);
			window.removeEventListener(OAUTH_UPDATED_EVENT, handleOAuthEvent);
		}
	}, []);

	const saveOauth = (newToken: OauthData | null) => {
		setOauthDataState( () => {
			if (newToken) {
				setOauthData( newToken );
				window.dispatchEvent(new CustomEvent(OAUTH_UPDATED_EVENT, { detail: newToken }));
				return newToken;
			}
			clearOauthData();
			window.dispatchEvent(new CustomEvent(OAUTH_UPDATED_EVENT, { detail: initialOauthState }));
			return initialOauthState;
		} );
	};

	const clearOauth = () => {
		setOauthDataState( () => {
			clearOauthData();
			window.dispatchEvent(new CustomEvent(OAUTH_UPDATED_EVENT, { detail: initialOauthState }));
			return initialOauthState;
		} );
	}

	return (
		<AuthContext.Provider value={ {
			oauth,
			saveOauth,
			clearOauth,
		} }>
			{ children }
		</AuthContext.Provider>
	);
};

const useOauth = (): AuthContextType => {
	const context = useContext<AuthContextType>(AuthContext);
	if ( ! context ) {
		throw new Error('useAuth must be used within an AuthProvider');
	}

	return context;
};

export default useOauth;
